import { Component, input, computed, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import {
  Student,
  MiscInfo,
  PaymentInfo,
  PaymentInfoWithReferences,
  LegalConfirmation,
} from '@awo-care/shared-core';
import { IbanMaskPipe } from '../iban-mask/iban-mask.pipe';

// Interface for contract data from forms (customer-ui scenario)
export interface ContractFormData {
  miscInfo?: MiscInfo;
  paymentInfo?: PaymentInfo | PaymentInfoWithReferences;
  legal?: LegalConfirmation;
}

@Component({
  selector: 'app-contract-summary',
  imports: [MatCardModule, MatIconModule, IbanMaskPipe],
  template: `
    <div class="summary-container">
      @if(hasCreatedContract()) {
      <div class="success-message">
        <mat-icon class="success-icon">check_circle</mat-icon>
        <div class="message-content">
          <h2 i18n="@@contract-summary.congratulations">
            Herzlichen Glückwunsch!
          </h2>
          <p i18n="@@contract-summary.contract-valid-part1">
            Ihr Vertrag ist ab sofort gültig
          </p>
          <p i18n="@@contract-summary.contract-valid-part2">
            Sie erhalten in Kürze weitere Informationen direkt von der
            Einrichtung.
          </p>
        </div>
      </div>
      } @if(showMeaslesWarning()) {
      <div class="warning-message">
        <mat-icon class="warning-icon">warning</mat-icon>
        <div class="message-content">
          <h2 i18n="@@contract-summary.measles-protection-required">
            Masernschutz erforderlich
          </h2>
          <p i18n="@@contract-summary.no-measles-vaccination-warning">
            Für Ihr Kind ist keine Masernimpfung vorhanden. Eine Aufnahme ist
            nur nach Vorlage einer Unbedenklichkeitsbescheinigung des
            Gesundheitsamts möglich.
          </p>
        </div>
      </div>
      }
      <mat-card appearance="outlined">
        <mat-card-header>
          <mat-icon mat-card-avatar>medical_services</mat-icon>
          <mat-card-title i18n="@@contract-summary.emergency-care"
            >Notfall & Betreuung</mat-card-title
          >
        </mat-card-header>
        <mat-card-content>
          <dl class="info-list">
            <div
              class="section-title"
              i18n="@@contract-summary.health-insurance"
            >
              Krankenversicherung
            </div>
            <div class="info-item">
              <dt i18n="@@contract-summary.insurance-name">
                Name der Krankenkasse
              </dt>
              <dd>{{ miscInfo()?.healthInsuranceName }}</dd>
            </div>
            <div class="info-item">
              <dt i18n="@@contract-summary.insured-with">Mitversichert bei</dt>
              <dd>{{ miscInfo()?.healthInsuranceWith }}</dd>
            </div>
            <div
              class="section-title"
              i18n="@@contract-summary.emergency-contact"
            >
              Notfallkontakt
            </div>
            <div class="info-item">
              <dt i18n="@@contract-summary.name">Name</dt>
              <dd>{{ miscInfo()?.emergencyName }}</dd>
            </div>
            <div class="info-item">
              <dt i18n="@@contract-summary.phone">Telefon</dt>
              <dd>{{ miscInfo()?.emergencyPhone }}</dd>
            </div>
            <div
              class="section-title"
              i18n="@@contract-summary.measles-protection"
            >
              Masernschutz
            </div>
            <div class="info-item">
              <dt i18n="@@student-info-form.measles-vaccination-available">
                Masernimpfung vorhanden
              </dt>
              <dd>
                @if(studentData()?.healthInfo?.measlesVaccination) {
                <span i18n="@@common.yes">Ja</span>
                } @else {
                <span i18n="@@common.no">Nein</span>
                } @if(!studentData()?.healthInfo?.measlesVaccination) {
                <span
                  class="warning-text"
                  i18n="@@student-info-form.health-office-certificate-required"
                >
                  (Unbedenklichkeitsbescheinigung erforderlich)</span
                >
                }
              </dd>
            </div>
            <!-- DEACTIVATED: After school arrangement section
            <div class="section-title">Betreuungszeit</div>
            <div class="info-item">
              <dt>Ende der Betreuungszeit</dt>
              <dd>
                {{
                  getAfterSchoolArrangementLabel(
                    miscInfo()?.afterSchoolArrangement
                  )
                }}
              </dd>
            </div>
            @if(miscInfo()?.afterSchoolArrangementDetails) {
            <div class="info-item">
              <dt>Namen der Abholberechtigten</dt>
              <dd>
                {{ miscInfo()?.afterSchoolArrangementDetails }}
              </dd>
            </div>
            }
            -->
          </dl>
        </mat-card-content>
      </mat-card>

      @if (!hideBankData()) {
      <mat-card appearance="outlined">
        <mat-card-header>
          <mat-icon mat-card-avatar>payments</mat-icon>
          <mat-card-title i18n="@@contract-summary.payment-information"
            >Zahlungsinformationen</mat-card-title
          >
        </mat-card-header>
        <mat-card-content>
          <dl class="info-list">
            <div class="section-title" i18n="@@contract-summary.account-holder">
              Kontoinhaber
            </div>
            <div class="info-item">
              <dt i18n="@@contract-summary.name">Name</dt>
              <dd>{{ accountHolderName() }}</dd>
            </div>

            @if(addressInfo()) {
            <div class="section-title" i18n="@@contract-summary.address">
              Adresse
            </div>
            <div class="info-item">
              <dt i18n="@@contract-summary.street">Straße</dt>
              <dd>{{ addressInfo()?.street }}</dd>
            </div>
            <div class="info-item">
              <dt i18n="@@contract-summary.zip-city">PLZ / Ort</dt>
              <dd>
                {{ addressInfo()?.zipCode }}
                {{ addressInfo()?.city }}
              </dd>
            </div>
            }

            <div
              class="section-title"
              i18n="@@contract-summary.bank-connection"
            >
              Bankverbindung
            </div>
            <div class="info-item">
              <dt i18n="@@contract-summary.iban">IBAN</dt>
              <dd>
                @if(isNewPaymentStructure()) {
                {{ iban() }}
                } @else {
                {{ iban() | ibanMask }}
                }
              </dd>
            </div>
            <div class="info-item">
              <dt i18n="@@contract-summary.bic">BIC</dt>
              <dd>{{ bic() }}</dd>
            </div>
            @if(bankName()) {
            <div class="info-item">
              <dt i18n="@@contract-summary.bank">Bank</dt>
              <dd>{{ bankName() }}</dd>
            </div>
            }
          </dl>
        </mat-card-content>
      </mat-card>
      }

      <mat-card appearance="outlined">
        <mat-card-header>
          <mat-icon mat-card-avatar>gavel</mat-icon>
          <mat-card-title i18n="@@contract-summary.business-terms"
            >Beitrags- und Geschäftsordnung</mat-card-title
          >
        </mat-card-header>
        <mat-card-content>
          <dl class="info-list">
            <div class="info-item">
              <dt i18n="@@contract-summary.consent-accepted">
                Einverständniserklärung akzeptiert&nbsp;
              </dt>
              <dd>
                {{ legalInfo()?.hasAccepted ? '✔' : '❌' }}
              </dd>
            </div>
          </dl>
        </mat-card-content>
      </mat-card>

      <mat-card appearance="outlined">
        <mat-card-header>
          <mat-icon mat-card-avatar>info</mat-icon>
          <mat-card-title i18n="@@contract-summary.cancellation-title"
            >Änderungen/ordentliche und außerordentliche Kündigung</mat-card-title
          >
        </mat-card-header>
        <mat-card-content>
          <p class="cancellation-notice" i18n="@@contract-summary.cancellation-text">
            Die Kündigung durch den Erziehungsberechtigten ist schriftlich der AWO Perspektiven gGmbH zuzustellen. Fristgerechte Vertragsänderungen (Modulwechsel) oder -kündigungen sind jeweils bis spätestens 6 Wochen vor Ende des Schulhalbjahres (zum 1.2 bzw. 31.07. eines Jahres) mitzuteilen – dies muss schriftlich erfolgen.
          </p>
        </mat-card-content>
      </mat-card>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .summary-container {
        display: grid;
        gap: 1.5rem;
        margin: 0 auto;
        padding-top: 1rem;
        padding-bottom: 1rem;
        max-width: 800px;
      }

      .success-message {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
        background: var(--theme-success);
        color: white;
        padding: 1.5rem;
        border-radius: var(--border-radius, 8px);
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      }

      .no-data-message {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
        background: var(--theme-info);
        color: white;
        padding: 1.5rem;
        border-radius: var(--border-radius, 8px);
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      }

      .warning-message {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
        background: var(--theme-warn);
        color: white;
        padding: 1.5rem;
        border-radius: var(--border-radius, 8px);
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      }

      .warning-icon {
        border-radius: 50%;
        padding: 0.5rem;
        font-size: 2rem;
        height: 2rem;
        width: 2rem;
        flex-shrink: 0;
      }

      .success-icon {
        border-radius: 50%;
        padding: 0.5rem;
        font-size: 2rem;
        height: 2rem;
        width: 2rem;
        flex-shrink: 0;
      }

      .message-content {
        flex: 1;
      }

      .message-content h2 {
        font-size: 1.5rem;
        font-weight: 500;
        margin: 0 0 0.5rem 0;
      }

      .message-content p {
        margin: 0;
        line-height: 1.5;
        opacity: 0.9;
      }
      .info-list {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        margin: 0;
      }

      .info-item {
        display: flex;
        line-height: 1.5;
      }

      dt {
        min-width: 300px;
        font-weight: 500;
        color: var(--theme-regularTextColor);
      }

      dd {
        flex: 1;
        margin: 0;
      }

      .section-title {
        font-size: 1.1rem;
        font-weight: 500;
        color: var(--theme-regularTextColor);
        margin: 1.5rem 0 1rem;
      }

      .warning-text {
        color: var(--theme-warn);
        font-weight: 500;
        font-size: 0.9rem;
      }

      .cancellation-notice {
        line-height: 1.6;
        color: rgba(0, 0, 0, 0.7);
        margin: 0;
      }

      @media (max-width: 600px) {
        .success-message {
          padding: 1rem;
        }

        .message-content h2 {
          font-size: 1.25rem;
        }

        .message-content p {
          font-size: 0.9rem;
        }

        .info-item {
          flex-direction: column;
          padding-bottom: 0.75rem;
          margin-bottom: 0.75rem;
          border-bottom: 1px solid var(--theme-default);
        }

        dt {
          min-width: 100%;
          margin-bottom: 0.25rem;
          color: var(--theme-regularTextColor);
        }

        mat-card {
          box-shadow: none;
          border-radius: 0;
          border-bottom: 1px solid var(--theme-default);
        }

        mat-card-content {
          padding: 0 1rem;
        }

        .section-title {
          margin-top: 2rem;
        }
      }
    `,
  ],
})
export class ContractSummaryComponent implements OnInit {
  studentData = input<Student>();
  contractData = input<ContractFormData>();
  /** Blendet Kontoinhaber, Adresse und Bankverbindung aus (Teamleitung, #1271). */
  hideBankData = input(false);

  // Computed signals that prioritize contract form data over student data
  miscInfo = computed(() => {
    return this.contractData()?.miscInfo || this.studentData()?.miscInfo;
  });

  paymentInfo = computed(() => {
    const contractPayment = this.contractData()?.paymentInfo;
    const studentPayment = this.studentData()?.paymentInfo;
    const result = contractPayment || studentPayment;

    return result;
  });

  // Helper to check if payment info is new structure (PaymentInfoWithReferences)
  isNewPaymentStructure = computed(() => {
    const payment = this.paymentInfo();
    return payment && 'paymentMethodId' in payment;
  });

  // Helper to get account holder name (works for both old and new structure)
  accountHolderName = computed(() => {
    const payment = this.paymentInfo();
    if (!payment) return '';

    if (this.isNewPaymentStructure()) {
      const newPayment = payment as PaymentInfoWithReferences;
      return newPayment.accountHolder || '';
    } else {
      const oldPayment = payment as PaymentInfo;
      return `${oldPayment.firstName || ''} ${
        oldPayment.lastName || ''
      }`.trim();
    }
  });

  // Helper to get IBAN (works for both old and new structure)
  iban = computed(() => {
    const payment = this.paymentInfo();
    if (!payment) return '';

    if (this.isNewPaymentStructure()) {
      const newPayment = payment as PaymentInfoWithReferences;
      return newPayment.maskedIban || '';
    } else {
      const oldPayment = payment as PaymentInfo;
      return oldPayment.bankAccountInfo?.iban || '';
    }
  });

  // Helper to get BIC (works for both old and new structure)
  bic = computed(() => {
    const payment = this.paymentInfo();
    if (!payment) return '';

    if (this.isNewPaymentStructure()) {
      const newPayment = payment as PaymentInfoWithReferences;
      return newPayment.bic || '';
    } else {
      const oldPayment = payment as PaymentInfo;
      return oldPayment.bankAccountInfo?.bic || '';
    }
  });

  // Helper to get bank name (works for both old and new structure)
  bankName = computed(() => {
    const payment = this.paymentInfo();
    if (!payment) return '';

    if (this.isNewPaymentStructure()) {
      const newPayment = payment as PaymentInfoWithReferences;
      return newPayment.bankName || '';
    } else {
      const oldPayment = payment as PaymentInfo;
      return oldPayment.bankAccountInfo?.bankName || '';
    }
  });

  // Helper to get address info (only available in old structure)
  addressInfo = computed(() => {
    const payment = this.paymentInfo();
    if (!payment || this.isNewPaymentStructure()) return null;

    const oldPayment = payment as PaymentInfo;
    return oldPayment.addressInfo || null;
  });

  legalInfo = computed(() => {
    return this.contractData()?.legal || this.studentData()?.contract?.legal;
  });

  // Helper computed to check if contract exists (for success message)
  hasCreatedContract = computed(() => {
    return !!this.studentData()?.contract?.createdOn;
  });

  // Helper computed to check if measles warning should be shown
  showMeaslesWarning = computed(() => {
    return this.studentData()?.healthInfo?.measlesVaccination === false;
  });

  ngOnInit(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // DEACTIVATED: After school arrangement label method
  // getAfterSchoolArrangementLabel(arrangement: string | undefined): string {
  //   switch (arrangement) {
  //     case 'picked_up':
  //       return 'Abholung';
  //     case 'independent_departure':
  //       return 'Selbständiger Nachhauseweg';
  //     case 'bus_transport':
  //       return 'Bus Transport';
  //     default:
  //       return 'Nicht angegeben';
  //   }
  // }
}
